import { DateTime } from 'luxon';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';

// MUI
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { showRefundEditDialog } from 'components/Dialogs/RefundEditDialog';
import { showRefundMatchDialog } from 'components/Dialogs/RefundMatchDialog';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
// DATA
import { useDispatch } from 'react-redux';

import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
// custom
import AmountField from 'components/QuickenControls/AmountField';
import { useAppSelector } from 'hooks/redux';

const useStyles = makeStyles()(() => ({
  lineRoot: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginLeft: 'auto',
    justifyContent: 'center',
  },
  // Drawer layout: left-align the refund content instead of pushing it to the right edge.
  lineRootLeft: {
    alignItems: 'flex-start',
    marginLeft: 0,
  },
}));

// Refund Line

// Accepts a txn record and displays the correct refund options;
function RefundLine({
  txn,
  disabled,
  rootModalDrawer,
  leftAlign = false,
}: {
  txn: CashFlowTransaction;
  disabled: boolean;
  rootModalDrawer?: RootModalDrawerOptions;
  /** Drawer layout: left-align the refund content instead of the default right alignment. */
  leftAlign?: boolean;
}) {
  const { classes, cx } = useStyles();
  const lineRootClass = cx(classes.lineRoot, leftAlign && classes.lineRootLeft);
  const dispatch = useDispatch();

  const refundTracker = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'refundTracker'));

  const refundActiveRelatedTxns = useAppSelector((state) =>
    transactionsSelectors.getRefundActiveRelatedTxns(state, txn.id || ''),
  );
  const refundCompleteRelatedTxns = useAppSelector((state) =>
    transactionsSelectors.getRefundCompleteRelatedTxns(state, txn.id || ''),
  );
  const isDownloadedTxnMatchedToSalesReceipt = useAppSelector((state) =>
    Boolean(
      txn.cpData &&
        transactionsUtils.isTxnMatchedToSalesReceipt({
          txnId: txn.id,
          transactionsById: transactionsSelectors.getResourcesById(state),
        }),
    ),
  );

  if (
    !refundTracker ||
    !txn ||
    (txn.amount > 0 && (!transactionsUtils.isBankDownloadedTxn(txn) || transactionsUtils.isMatched(txn))) ||
    disabled ||
    isDownloadedTxnMatchedToSalesReceipt
  ) {
    return null;
  }

  let refundSection;

  // already has refunded or tracked txns
  if (refundTracker && (refundActiveRelatedTxns.size || refundCompleteRelatedTxns.size)) {
    refundSection = (
      <div className={lineRootClass}>
        {/* Active Refunds */}
        {refundActiveRelatedTxns.toIndexedSeq().map((refundActiveTransaction) => (
          <Typography variant='captionRegular' key={refundActiveTransaction.id}>
            <Link
              onClick={() => {
                dispatch(
                  showRefundEditDialog({
                    id: 'refund-from-transaction',
                    rootModalDrawer,
                    transaction: refundActiveTransaction,
                  }),
                );
              }}
            >
              Refund being tracked for &nbsp;
              <AmountField value={refundActiveTransaction.amount} />
            </Link>
          </Typography>
        ))}

        {/* Completed Refunds */}
        {refundCompleteRelatedTxns.toIndexedSeq().map((refundCompleteTransaction) => (
          <Typography variant='captionRegular' key={refundCompleteTransaction.id}>
            <Link
              onClick={() => {
                dispatch(
                  showRefundEditDialog({
                    id: 'refund-from-transaction',
                    rootModalDrawer,
                    transaction: refundCompleteTransaction,
                  }),
                );
              }}
            >
              Refund complete
            </Link>
          </Typography>
        ))}
      </div>
    );
  } else {
    const refundState = transactionsUtils.getRefundState(txn);
    if (refundState === transactionsUtils.RefundState.COMPLETE) {
      refundSection = (
        <Typography variant='captionRegular' id='refund-complete'>
          Refund is complete.
        </Typography>
      );
    } else {
      const { payee, amount, accountId, coa, id } = txn;
      const posAmount = amount > 0;

      const handleClick = () => {
        if (posAmount && id) {
          dispatch(showRefundMatchDialog({ rootModalDrawer, transaction: txn }));

          // promise usage example
          // new Promise((resolve) => dispatch(rootUiActions.createDialog(rootUiTypes.mkRootUiData({
          //   id: 'refund-match',
          //   type: DIALOG_TYPE_REFUND_MATCH,
          //   allowNesting: true,
          //   props: ImmutableMap({
          //     transaction: txn,
          //     resolve,
          //   })
          // })))).then((reason) => {
          //   if (reason === 'refundMatched') {
          //   }
          // }).catch((error) => assert(false, error));

          tracker.track(tracker.events.refundManualMatch, { location: 'transaction details' });
        } else {
          dispatch(
            showRefundEditDialog({
              id: 'refund-from-transaction',
              rootModalDrawer,
              transaction: {
                postedOn: DateTime.now().startOf('day').plus({ days: 3 }).toISODate(), // qcs match range [-5:+3] days
                payee,
                amount: -amount,
                accountId,
                coa,
                subtypeRelatedTxn: { id: txn.id ?? undefined },
              },
            }),
          );
        }
      };

      refundSection =
        posAmount && !id ? null : (
          <Typography className={lineRootClass} variant='captionRegular' id='refund-line'>
            <Link
              tabIndex={0}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Enter') {
                  handleClick();
                }
              }}
              onClick={handleClick}
              id='refund-button'
            >
              {posAmount ? 'Match refund' : 'Track a refund'}
            </Link>
          </Typography>
        );
    }
  }

  return refundTracker && refundSection;
}

export default RefundLine;
